import React, { useEffect, useState } from "react";

/**
 * ReadingProgress — thin orange bar fixed to the top of the viewport that
 * fills as the reader scrolls through the article.
 *
 * `target` (optional) is a CSS selector for the article container. If provided,
 * the progress is computed relative to that element's scroll range instead of
 * the whole page — nicer for pages with lots of below-the-fold content
 * (newsletters, related posts, footer). When omitted we use the whole page.
 */
export default function ReadingProgress({ target = null, className = "" }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const update = () => {
      const el = target ? document.querySelector(target) : null;
      let pct = 0;
      if (el) {
        const rect = el.getBoundingClientRect();
        const total = rect.height - window.innerHeight;
        const scrolled = -rect.top;
        pct = total > 0 ? (scrolled / total) * 100 : 0;
      } else {
        const doc = document.documentElement;
        const total = doc.scrollHeight - window.innerHeight;
        pct = total > 0 ? (window.scrollY / total) * 100 : 0;
      }
      setProgress(Math.max(0, Math.min(100, pct)));
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [target]);

  return (
    <div
      data-testid="reading-progress"
      className={`fixed top-0 left-0 right-0 z-[60] h-1 bg-transparent pointer-events-none ${className}`}
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Reading progress"
    >
      <div
        className="h-full bg-primary transition-[width] duration-100 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
